import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { allProducts } from '@/utils/productsData';
import { Product } from '@/types/product';
import '@/assets/css/product-detail.css';

export const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // 全製品から該当するIDの製品を検索
  const findProduct = (): Product | undefined => {
    for (const category of Object.values(allProducts)) {
      const product = category.find((p) => p.id === id);
      if (product) return product;
    }
    return undefined;
  };

  const product = findProduct();

  // 関連製品を取得（同じカテゴリの他の製品、最大3つ）
  const getRelatedProducts = (): Product[] => {
    if (!product) return [];
    const categoryProducts = allProducts[product.category];
    return categoryProducts
      .filter((p) => p.id !== product.id)
      .slice(0, 3);
  };

  const relatedProducts = getRelatedProducts();

  if (!product) {
    return (
      <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
        <h2>製品が見つかりません</h2>
        <p style={{ marginTop: '1rem', marginBottom: '2rem' }}>
          指定された製品は存在しないか、削除された可能性があります。
        </p>
        <Link to="/#lineup" className="btn btn-primary">
          製品ラインナップに戻る
        </Link>
      </div>
    );
  }

  const getCategoryLabel = (category: string): string => {
    const labels: Record<string, string> = {
      indoor: 'Indoor',
      outdoor: 'Outdoor',
      square: 'Square',
      accessories: 'アクセサリー',
    };
    return labels[category] || category;
  };

  return (
    <div className="product-detail-page">
      {/* パンくずリスト */}
      <nav className="breadcrumb">
        <div className="container">
          <ol className="breadcrumb-list">
            <li className="breadcrumb-item">
              <Link to="/">ホーム</Link>
            </li>
            <li className="breadcrumb-separator">›</li>
            <li className="breadcrumb-item">
              <Link to="/#lineup">製品ラインナップ</Link>
            </li>
            <li className="breadcrumb-separator">›</li>
            <li className="breadcrumb-item">
              <span>{getCategoryLabel(product.category)}</span>
            </li>
            <li className="breadcrumb-separator">›</li>
            <li className="breadcrumb-item">{product.name}</li>
          </ol>
        </div>
      </nav>

      {/* 製品ヒーローセクション */}
      <section className="product-hero">
        <div className="container">
          <div className="product-hero-content">
            <div className="product-image-main">
              <img src={product.image} alt={product.name} />
            </div>
            <div className="product-info-main">
              <div className="product-category-badge">
                {getCategoryLabel(product.category)} Series
              </div>
              <h1 className="product-title">{product.name}</h1>
              <p className="product-subtitle">{product.size}</p>
              <p className="product-description">{product.description}</p>

              <div className="product-key-specs">
                <div className="spec-item">
                  <div className="spec-label">輝度</div>
                  <div className="spec-value">{product.specs.brightness}</div>
                </div>
                <div className="spec-item">
                  <div className="spec-label">重量</div>
                  <div className="spec-value">{product.specs.weight}</div>
                </div>
                <div className="spec-item">
                  <div className="spec-label">
                    {product.category === 'outdoor' ? '防水規格' : '厚さ'}
                  </div>
                  <div className="spec-value">{product.specs.thickness}</div>
                </div>
              </div>

              <div className="cta-buttons">
                <a href="/#contact" className="btn btn-primary btn-large">
                  お問い合わせ
                </a>
                <a href="#specifications" className="btn btn-outline btn-large">
                  詳細スペック
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 製品詳細セクション */}
      <section className="product-detail">
        <div className="container">
          <div className="product-sections">
            {/* 特長セクション */}
            {product.features && product.features.length > 0 && (
              <div className="product-section">
                <div className="section-header">
                  <h2 className="section-title">主な特長</h2>
                  <p className="section-description">
                    この製品の主な特長と利点をご紹介します。
                  </p>
                </div>
                <div className="features-grid">
                  {product.features.map((feature, index) => (
                    <div key={index} className="feature-card">
                      <h3 className="feature-title">{feature.title}</h3>
                      <p className="feature-description">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* スペックセクション */}
            {product.detailedSpecs && product.detailedSpecs.length > 0 && (
              <div className="product-section" id="specifications">
                <div className="section-header">
                  <h2 className="section-title">詳細スペック</h2>
                  <p className="section-description">
                    製品の詳細な仕様をご確認いただけます。
                  </p>
                </div>
                <div className="specs-table">
                  <table>
                    <thead>
                      <tr>
                        <th>項目</th>
                        <th>仕様</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.detailedSpecs.map(([label, value], index) => (
                        <tr key={index}>
                          <td>{label}</td>
                          <td>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 関連製品セクション */}
      {relatedProducts.length > 0 && (
        <section className="related-products">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">関連製品</h2>
              <p className="section-description">
                同カテゴリの他の製品もご検討ください。
              </p>
            </div>
            <div className="products-grid">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="related-product-card"
                >
                  <div className="related-product-image">
                    <img src={relatedProduct.image} alt={relatedProduct.name} />
                  </div>
                  <div className="related-product-info">
                    <h4 className="related-product-title">{relatedProduct.name}</h4>
                    <p className="related-product-size">{relatedProduct.size}</p>
                    <p className="related-product-desc">{relatedProduct.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
